import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';

// Keep the existing recorder/encoder. Own permission lifetime only for Radio.
export default function useScopedChatRecorder({ scoped, scope, onBeforeCapture }) {
  const [stream] = useState(() => scoped && typeof MediaStream !== 'undefined' ? new MediaStream() : null);
  const lifetime = useRef({ scope, epoch: 0, alive: true, busy: false });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [acceptedUrl, setAcceptedUrl] = useState(null);
  // This render's recorder start/onStop closures share the same take identity.
  let take;
  const stopTracks = () => stream?.getTracks().forEach((track) => { track.stop(); stream.removeTrack(track); });
  const recorder = useReactMediaRecorder({ audio: true, customMediaStream: stream || undefined,
    onStop: (url) => {
      if (!scoped) return;
      if (!take || !take.owner.alive || lifetime.current !== take.owner ||
          take.epoch !== take.owner.epoch || take.owner.discard) URL.revokeObjectURL(url);
      else { take.owner.busy = false; setAcceptedUrl(url); }
    },
  });
  const recorderRef = useRef(recorder);
  recorderRef.current = recorder;

  useLayoutEffect(() => {
    lifetime.current = { scope, epoch: lifetime.current.epoch + 1, alive: true, busy: false };
    if (scoped) setAcceptedUrl(null);
    return () => {
      if (!scoped) return;
      lifetime.current.alive = false;
      lifetime.current.discard = true;
      lifetime.current.epoch += 1;
      recorderRef.current.stopRecording();
      stopTracks();
      recorderRef.current.clearBlobUrl();
    };
    // Stream identity stays fixed for the recorder's captured customMediaStream.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, scoped]);

  const cancel = () => {
    lifetime.current.epoch += 1;
    lifetime.current.discard = true;
    lifetime.current.busy = false;
    recorder.stopRecording();
    stopTracks();
    recorder.clearBlobUrl();
    setAcceptedUrl(null);
    setPending(false);
  };
  const start = async () => {
    if (!scoped) return recorder.startRecording();
    if (lifetime.current.busy) return;
    if (!stream || !navigator.mediaDevices?.getUserMedia) {
      setError('Microphone recording is unavailable in this browser.');
      return;
    }
    const owner = lifetime.current;
    const epoch = ++owner.epoch;
    owner.busy = true;
    owner.discard = false;
    setError('');
    setPending(true);
    onBeforeCapture?.();
    try {
      const acquired = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!owner.alive || lifetime.current !== owner || epoch !== owner.epoch) {
        acquired.getTracks().forEach((track) => track.stop());
        return;
      }
      stopTracks();
      acquired.getTracks().forEach((track) => stream.addTrack(track));
      take = { owner, epoch };
      await recorder.startRecording();
      if (!owner.alive || epoch !== owner.epoch) { recorder.stopRecording(); stopTracks(); }
    } catch (err) {
      if (owner.alive && epoch === owner.epoch) {
        owner.busy = false;
        stopTracks();
        setError('Microphone permission was denied or recording could not start. You can retry.');
      }
    } finally {
      if (owner.alive && epoch === owner.epoch) setPending(false);
    }
  };
  const stop = () => {
    if (pending) { cancel(); return; }
    recorder.stopRecording();
    lifetime.current.busy = false;
  };
  useEffect(() => {
    if (!scoped || !recorder.error) return;
    lifetime.current.busy = false;
    stopTracks();
    setPending(false);
    setError('Recording could not start. Check your microphone and retry.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, recorder.error]);
  useEffect(() => {
    if (!scoped || recorder.status !== 'recording') return undefined;
    const timer = setTimeout(() => { recorderRef.current.stopRecording(); lifetime.current.busy = false; }, 300000);
    return () => clearTimeout(timer);
  }, [scoped, recorder.status]);
  return { ...recorder, startRecording: start, stopRecording: stop,
    clearBlobUrl: () => { recorder.clearBlobUrl(); if (scoped) setAcceptedUrl(null); },
    cancelRecording: cancel, permissionPending: pending, captureError: error,
    isCaptureBusy: () => scoped ? lifetime.current.busy : recorder.status === 'recording',
    mediaBlobUrl: scoped ? acceptedUrl : recorder.mediaBlobUrl,
  };
}
